import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/delivery.scss";

function Delivery() {
  const formatMoney = (value) =>
    new Intl.NumberFormat(siteConfig.locale, {
      style: "currency",
      currency: siteConfig.currency,
    }).format(Number(value));

  const standardCost = formatMoney(siteConfig.shippingCost);
  const expressCost =
    siteConfig.expressShippingCost == null
      ? "Calculated at checkout"
      : formatMoney(siteConfig.expressShippingCost);

  return (
    <div className="delivery-page">
      <nav className="delivery-page__breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Delivery</span>
      </nav>

      <header className="delivery-page__header">
        <p className="delivery-page__eyebrow">Shipping information</p>
        <h1>From our door to your next adventure.</h1>
        <p>
          Clear delivery choices, order updates and straightforward pricing —
          so you can spend less time waiting and more time planning.
        </p>
      </header>

      <main className="delivery-page__main">
        <section
          className="delivery-page__overview"
          aria-labelledby="delivery-overview"
        >
          <div>
            <p className="delivery-page__section-label">At a glance</p>
            <h2 id="delivery-overview">Delivery options</h2>
          </div>
          <div className="delivery-page__overview-copy">
            <p>
              Tracked delivery costs <strong>{standardCost}</strong> per order.
              The destination-specific arrival estimate is confirmed at checkout.
            </p>
            <p>
              The business contact address is in {siteConfig.countryName}. The
              dispatch location, available carrier and service level are confirmed
              for each order and should not be inferred from that address.
            </p>
          </div>
        </section>

        <section className="delivery-page__table-section" aria-labelledby="delivery-table-title">
          <h2 className="delivery-page__visually-hidden" id="delivery-table-title">
            Delivery services and costs
          </h2>
          <div className="delivery-page__table-wrap">
            <table>
              <caption>Delivery services</caption>
              <thead>
                <tr>
                  <th scope="col">Service</th>
                  <th scope="col">Estimated arrival</th>
                  <th scope="col">Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Standard tracked</th>
                  <td>3–5 working days</td>
                  <td>
                    {standardCost}
                  </td>
                </tr>
                <tr>
                  <th scope="row">Express tracked</th>
                  <td>1–2 working days</td>
                  <td>{expressCost}</td>
                </tr>
                <tr>
                  <th scope="row">Remote locations</th>
                  <td>5–8 working days</td>
                  <td>Confirmed at checkout</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="delivery-page__table-note">
            Working days exclude weekends and public holidays. Cut-off times
            and location-specific estimates appear before payment.
          </p>
        </section>

        <section className="delivery-page__journey" aria-labelledby="order-journey">
          <div className="delivery-page__section-heading">
            <p className="delivery-page__section-label">What happens next</p>
            <h2 id="order-journey">Your order journey</h2>
          </div>
          <ol>
            <li>
              <span>01</span>
              <div>
                <h3>Order confirmed</h3>
                <p>We send an email as soon as your order is accepted.</p>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <h3>Carefully packed</h3>
                <p>Your items are checked and prepared for dispatch.</p>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <h3>Ready to track</h3>
                <p>A tracking link is emailed when the carrier has your parcel.</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="delivery-page__faq" aria-labelledby="delivery-faq">
          <div className="delivery-page__section-heading">
            <p className="delivery-page__section-label">Useful details</p>
            <h2 id="delivery-faq">Delivery questions</h2>
          </div>
          <div className="delivery-page__faq-list">
            <details>
              <summary>Can I change my address after ordering?</summary>
              <p>
                Contact us as soon as possible. We cannot guarantee changes
                once packing has started, but our team will always check what
                is possible.
              </p>
            </details>
            <details>
              <summary>Why has my order arrived in separate parcels?</summary>
              <p>
                Items may occasionally leave from different locations. Each
                parcel receives its own tracking update and there is no extra
                delivery charge.
              </p>
            </details>
            <details>
              <summary>What should I do if tracking has not moved?</summary>
              <p>
                Carriers can take a working day to refresh tracking. If there
                is still no update after that, email
                {" "}
                <a href={"mailto:" + siteConfig.contact.email}>
                  {siteConfig.contact.email}
                </a>
                .
              </p>
            </details>
          </div>
        </section>

        <section className="delivery-page__support" aria-labelledby="delivery-support">
          <div>
            <p className="delivery-page__section-label">Need a hand?</p>
            <h2 id="delivery-support">We will help you find your parcel.</h2>
          </div>
          <div className="delivery-page__support-actions">
            <Link className="delivery-page__button" to="/contact">
              Contact customer care
            </Link>
            <Link className="delivery-page__text-link" to="/returns">
              Read our returns policy
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Delivery;
